import { Injectable } from '@angular/core';
import { PoliciesHelperService } from '@core/services/core/policies-helper.service';
import { ClientAreaConfigService } from '@core/services/http-services/client-area-config.service';
import { Policy, PolicyBaseType } from '@models/policy';
import {
  ClientAreaCardConfig, ClientAreaCardLocalConfig, ClientAreaCardRemoteConfig,
  ClientAreaConfigurableCard, ClientAreaErrorCard, ClientAreaPoliciesGroup, ClientAreaVisibleCard, PolicyBaseEntry, processDisabledClientAreaPolicyCard
} from '@models/products';
import { filterUniqueBasicTypesInList, flat, joinStrings } from '@utils/utils';
import { map, switchMap, toArray } from 'rxjs/operators';
import { FormatMomentDatePipe } from '../../../shared/pipes/format-moment-date.pipe';

@Injectable({
  providedIn: 'root'
})
export class ClientAreaConfigHelperService {

  constructor(
    private clientAreaConfigService: ClientAreaConfigService,
    private policiesHelper: PoliciesHelperService,
    private formatDatePipe: FormatMomentDatePipe
  ) { }

  getConfigurableCards() {
    return this.getCardsConfig().pipe(
      map((cardsConfigList: ClientAreaCardConfig[]) => {
        return this.getConfigurableCardsByConfig(cardsConfigList);
      })
    );
  }

  private getConfigurableCardsByConfig(cardsConfigList: ClientAreaCardConfig[]) {
    return cardsConfigList.map(cardConfig => this.createCardByConfig(cardConfig));
  }

  getCardsConfig() {
    const cardsLocalConfig = this.clientAreaConfigService.getCardsLocalConfig();
    return this.clientAreaConfigService.getCardsRemoteConfig().pipe(
      map((cardsRemoteConfig) => {
        return this.mergeConfigs(cardsLocalConfig, cardsRemoteConfig);
      })
    );
  }

  getUniquePolicyBaseTypesInCardsConfig() {
    return this.getCardsConfig().pipe(
      switchMap(cardsConfig => cardsConfig.map(cardConfig => cardConfig.policyBaseTypes)),
      toArray(),
      map(policyBaseTypes => filterUniqueBasicTypesInList(flat(policyBaseTypes)) as PolicyBaseType[]));
  }

  mergeConfigs(
    localConfigList: ClientAreaCardLocalConfig[],
    remoteConfigList: ClientAreaCardRemoteConfig[]
  ) {
    if (!localConfigList || !localConfigList.length || !remoteConfigList || !remoteConfigList.length) {
      return [];
    }
    return localConfigList.map(localConfig => {
      const matchedRemoteConfig = remoteConfigList.find(cardConfig => this.checkIsOfType(cardConfig, localConfig.cardType));
      if (!matchedRemoteConfig) {
        return null;
      }
      return this.getMergedCardConfig(localConfig, matchedRemoteConfig);
    }).filter(mergedConfig => !!mergedConfig);
  }

  private checkIsOfType(config: { cardType: string }, cardType: string) {
    return config?.cardType === cardType;
  }

  private getMergedCardConfig(
    localConfig: ClientAreaCardLocalConfig,
    remoteConfig: ClientAreaCardRemoteConfig
  ) {
    return {
      ...localConfig,
      ...remoteConfig
    } as ClientAreaCardConfig;
  }

  createCardByConfig(cardConfig: ClientAreaCardConfig) {
    return {
      cardConfig
    } as ClientAreaConfigurableCard;
  }

  getVisibleGroupCard(card: ClientAreaConfigurableCard) {
    if (!card?.groupedPoliciesLists) {
      return null;
    }
    return this.createGroupCard(card.cardConfig, card.groupedPoliciesLists);
  }

  createGroupCard(
    cardConfig: ClientAreaCardConfig,
    groupedPoliciesLists: ClientAreaPoliciesGroup
  ) {
    const groupCard: ClientAreaVisibleCard = {
      cardConfig,
      groupedCard: {
        hasAlert: this.groupCardHasAlert(groupedPoliciesLists),
        chipText: `${this.getGroupedPoliciesNumber(groupedPoliciesLists)} Pólizas`,
        groupedPoliciesLists,
      }
    };
    return groupCard;
  }

  getVisiblePolicyCards(card: ClientAreaConfigurableCard) {
    if (!card?.groupedPoliciesLists) {
      return null;
    }
    const plainPoliciesList = this.getPlainPoliciesListFromGroup(card.groupedPoliciesLists);
    return plainPoliciesList.map(policy => {
      return this.createPolicyCard(card.cardConfig, policy);
    });
  }

  createPolicyCard(
    cardConfig: ClientAreaCardConfig,
    policy: Policy
  ) {
    const policyCard: ClientAreaVisibleCard = {
      cardConfig,
      policyCard: {
        policy,
        hasAlert: this.checkIfPolicyHasAlert(policy),
      },
      resume: this.createPolicyResume(policy)
    };
    processDisabledClientAreaPolicyCard(policyCard.policyCard);
    return policyCard;
  }

  getErrorCardByConfig(cardConfig: ClientAreaCardConfig, isPartialError: boolean) {
    if (!cardConfig) {
      return null;
    }
    const errorCard: ClientAreaErrorCard = {
      cardType: cardConfig.cardType,
      icon: cardConfig.icon,
      order: cardConfig.order,
      groupingName: cardConfig.groupingName,
      errorText: cardConfig.errorText,
      isPartialError
    };
    return errorCard;
  }

  getGroupedPoliciesByConfig(cardConfig: ClientAreaCardConfig) {
    const policyBaseTypes = cardConfig?.policyBaseTypes?.length ? cardConfig.policyBaseTypes : [];
    return this.policiesHelper.getPolicyBaseEntries(policyBaseTypes).pipe(
      map((policyBaseEntries: PolicyBaseEntry[]) => {
        return this.filterPolicyBaseEntriesByCardType(policyBaseEntries, cardConfig.cardType);
      }),
      map((policyBaseEntries: PolicyBaseEntry[]) => {
        return this.createPoliciesGroupByBaseTypeEntries(policyBaseEntries, cardConfig);
      })
    );
  }

  private filterPolicyBaseEntriesByCardType(policyBaseEntries: PolicyBaseEntry[], cardType: string) {
    return (policyBaseEntries || []).map((policyBaseEntry: PolicyBaseEntry) => {
      if (!policyBaseEntry?.policiesListError && policyBaseEntry?.policiesList?.length) {
        policyBaseEntry.policiesList = this.filterPoliciesByCardType(policyBaseEntry.policiesList, cardType);
      }
      return policyBaseEntry;
    });
  }

  private filterPoliciesByCardType(policiesList: Policy[], cardType: string) {
    return (policiesList || []).filter((policy: Policy) => {
      return this.checkPolicyIsOfCardType(policy, cardType);
    });
  }

  private checkPolicyIsOfCardType(policy: Policy, cardType: string) {
    return policy?.configuration?.cardConfiguration?.type === cardType;
  }


  private checkIfPolicyHasAlert(policy: Policy) {
    return policy.pendingReceipts;
  }

  private groupCardHasAlert(groupedPoliciesLists: ClientAreaPoliciesGroup) {
    if (!groupedPoliciesLists?.policyBaseEntries?.length) {
      return false;
    }
    return groupedPoliciesLists.policyBaseEntries.some(policyBaseEntry => {
      if (!policyBaseEntry.policiesList) {
        return false;
      }
      return policyBaseEntry.policiesList.some(policy => this.checkIfPolicyHasAlert(policy));
    });
  }

  createPoliciesGroupByBaseTypeEntries(policyBaseEntries: PolicyBaseEntry[], cardConfig: ClientAreaCardConfig) {
    const entries = policyBaseEntries || [];
    const groupedPolicies = {
      policyBaseEntries: entries,
      hasErrors: entries.some(entry => entry?.policiesListError)
    } as ClientAreaPoliciesGroup;
    this.setCardConfigToPoliciesGroup(groupedPolicies, cardConfig);
    return groupedPolicies;
  }

  private setCardConfigToPoliciesGroup(groupedPolicies: ClientAreaPoliciesGroup, cardConfig: ClientAreaCardConfig) {
    if (!groupedPolicies?.policyBaseEntries?.length) {
      return;
    }
    groupedPolicies.policyBaseEntries.forEach(policyBaseEntry => {
      if (!policyBaseEntry?.policiesList?.length) {
        return;
      }
      policyBaseEntry.policiesList.forEach(policy => {
        this.setCardConfigToPolicy(policy, cardConfig);
      });
    });
  }

  private setCardConfigToPolicy(policy: Policy, cardConfig: ClientAreaCardConfig) {
    if (policy) {
      policy.clientAreaCardConfig = cardConfig;
    }
  }

  getGroupedPoliciesNumber(groupedPolicies: ClientAreaPoliciesGroup) {
    if (!groupedPolicies?.policyBaseEntries?.length) {
      return 0;
    }
    return groupedPolicies.policyBaseEntries.reduce((accumulatedPoliciesNumber, currentPolicyBaseEntry) => {
      if (!currentPolicyBaseEntry?.policiesList?.length) {
        return accumulatedPoliciesNumber;
      }
      return accumulatedPoliciesNumber + currentPolicyBaseEntry.policiesList.length;
    }, 0);
  }

  getGroupedPoliciesNumberInCardsList(configurableCards: ClientAreaConfigurableCard[]) {
    return configurableCards.reduce((counter, card) => {
      if (!card) { return counter; }
      return counter + this.getGroupedPoliciesNumber(card.groupedPoliciesLists);
    }, 0);
  }

  getPlainPoliciesListFromGroup(groupedPolicies: ClientAreaPoliciesGroup) {
    if (!groupedPolicies?.policyBaseEntries?.length) {
      return [] as Policy[];
    }
    return groupedPolicies.policyBaseEntries.reduce((accumulatedPolicies, currentPolicyBaseEntry) => {
      if (!currentPolicyBaseEntry?.policiesList?.length) {
        return accumulatedPolicies;
      }
      return [...accumulatedPolicies, ...(currentPolicyBaseEntry.policiesList || [])];
    }, [] as Policy[]);
  }

  getNumberOfAvailablePolicies(visibleCardsList: ClientAreaVisibleCard[]) {
    return visibleCardsList
      .map(item => {
        if (item.groupedCard) {
          return item.groupedCard?.groupedPoliciesLists?.policyBaseEntries?.
            map(entry => entry.policiesList || []).reduce((prev, current) => [...prev, ...current] , []) || [];
        } else {
          return [item.policyCard?.policy];
        }
      })
      .reduce((prev, current) => [...prev, ...current] , [])
      .filter(policy => policy.configuration?.cardConfiguration?.consultations?.policyDetail)
      .length;
  }

  collectConfigurableCardsErrors(configurableCards: ClientAreaConfigurableCard[]) {
    const configurableCardsErrorsWithDuplicates = [];
    configurableCards.forEach(configurableCard => {
      configurableCard?.groupedPoliciesLists?.policyBaseEntries?.forEach(policyBaseEntry => {
        if (policyBaseEntry?.policiesListErrorDetail) {
          configurableCardsErrorsWithDuplicates.push(policyBaseEntry?.policiesListErrorDetail);
        }
      });
    });

    const errorsToRegister = [
      ...new Map(
        configurableCardsErrorsWithDuplicates.map(error => [error.type, error])
      ).values()
    ];

    return errorsToRegister;
  }

  doesAllBranchesHaveErrors(configurableCards: ClientAreaConfigurableCard[]) {
    if (configurableCards.length === 0) {
      return false;
    }
    return configurableCards.every(configurableCard => configurableCard.groupedPoliciesLists?.hasErrors);
  }

  private createPolicyResume(policy: Policy) {
    return joinStrings([policy?.configuration?.cardConfiguration?.principalDescription,
      policy?.configuration?.cardConfiguration?.secondaryDescription,
      policy?.configuration?.cardConfiguration?.tertiaryDescription,
      policy?.policyNumber,
      this.formatDatePipe.transform(policy?.expirationDate)], '\n');
  }

  areAllPoliciesListsEmpty(configurableCards: ClientAreaConfigurableCard[]) {
    if (!configurableCards) { return true; }
    return this.getGroupedPoliciesNumberInCardsList(configurableCards) === 0;
  }
}
